import { useMutation, useQuery } from '@apollo/client'
import { Tooltip } from '@mantine/core'
import { RefreshCw } from 'lucide-react'

import { useAuth } from 'src/auth'
import AdminOnlyDot from 'src/components/AdminContainer/AdminOnlyDot'
import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { SectionInfoIcon } from 'src/components/Feedback/SectionInfoIcon/SectionInfoIcon'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_SIGNATURE_REQUESTS,
  REFRESH_SIGNATURE_REQUESTS,
} from 'src/graphql/signatureRequests.graphql'
import { isAdmin } from 'src/lib/auth/permissions'
import { formatDate } from 'src/lib/formatters'

import {
  getNextSignerHint,
  getSignatureStatusLabel,
  getSignatureStatusVariant,
  getSignedProgress,
  getSignerDetails,
  NOBODY_LEFT,
  STATUS_LEGEND,
  WAITING_ON_LEGEND,
} from './signatureRequestHelpers'

type SignatureRequest = {
  signatureRequestId: string
  createdAt: string
  status: string
  packageName: string
  documentNames: string[]
  sentAt?: string | null
  completedAt?: string | null
  lastSyncedAt?: string | null
  signers: {
    name: string
    role: string
    status: string
    routingOrder: number
  }[]
}

type Props = {
  applicantId: string
}

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

// Widths are percentages and must sum to 100
const COLUMN_WIDTHS = {
  package: 26,
  status: 20,
  waitingOn: 20,
  sent: 17,
  completed: 17,
}

// Every row is one line, whatever the names: anything that does not fit is cut
// off and shown on hover instead.
const columns = [
  {
    key: 'package',
    width: COLUMN_WIDTHS.package,
    label: 'Package',
    render: (request: SignatureRequest) => {
      const count = request.documentNames.length
      return (
        <Tooltip
          multiline
          maw={320}
          label={
            <div className="flex flex-col">
              <span>{`${count} ${
                count === 1 ? 'document' : 'documents'
              }`}</span>
              <span>{request.documentNames.join(', ')}</span>
            </div>
          }
        >
          <span className="block truncate">{request.packageName}</span>
        </Tooltip>
      )
    },
  },
  {
    key: 'status',
    width: COLUMN_WIDTHS.status,
    label: (
      <span className="inline-flex items-center gap-1">
        Status
        <SectionInfoIcon
          ariaLabel="About e-signature statuses"
          tooltip={
            <div className="flex flex-col gap-1 text-xs">
              {STATUS_LEGEND.map(({ label, meaning }) => (
                <div key={label}>
                  <span className="font-medium">{label}</span>
                  <span>{` – ${meaning}`}</span>
                </div>
              ))}
            </div>
          }
        />
      </span>
    ),
    render: (request: SignatureRequest) => (
      <div className="flex min-w-0 max-w-full">
        <Tag
          variant={getSignatureStatusVariant(request.status)}
          size="xs"
          showCloseButton={false}
          wrap
          className="min-w-0 max-w-full"
        >
          <span className="min-w-0 break-words">
            {getSignatureStatusLabel(request)}
          </span>
        </Tag>
      </div>
    ),
  },
  {
    key: 'waitingOn',
    width: COLUMN_WIDTHS.waitingOn,
    label: (
      <span className="inline-flex items-center gap-1">
        Waiting on
        <SectionInfoIcon
          ariaLabel="About who the request is waiting on"
          tooltip={
            <div className="flex flex-col gap-1 text-xs">
              <div className="opacity-80">Hover a row for every signer.</div>
              {WAITING_ON_LEGEND.map(({ label, meaning }) => (
                <div key={label}>
                  <span className="font-medium">{label}</span>
                  <span>{` – ${meaning}`}</span>
                </div>
              ))}
            </div>
          }
        />
      </span>
    ),
    render: (request: SignatureRequest) => {
      const details = getSignerDetails(request.signers)
      const progress = getSignedProgress(request.signers)
      const cell = (
        <span className="block truncate">
          {getNextSignerHint(request) ?? NOBODY_LEFT}
        </span>
      )
      if (details.length === 0) return cell
      return (
        <Tooltip
          multiline
          maw={360}
          label={
            <div className="flex flex-col gap-1">
              {progress && <span className="font-medium">{progress}</span>}
              {details.map((signer, index) => (
                <div key={index} className="flex flex-col">
                  <span>{signer.name}</span>
                  <span className="opacity-80">
                    {signer.role
                      ? `${signer.role} · ${signer.status}`
                      : signer.status}
                  </span>
                </div>
              ))}
            </div>
          }
        >
          {cell}
        </Tooltip>
      )
    },
  },
  {
    key: 'sent',
    width: COLUMN_WIDTHS.sent,
    label: 'Sent',
    render: (request: SignatureRequest) =>
      formatDateTime(request.sentAt ?? request.createdAt),
  },
  {
    key: 'completed',
    width: COLUMN_WIDTHS.completed,
    label: 'Completed',
    render: (request: SignatureRequest) => formatDateTime(request.completedAt),
  },
]

// When the provider was last asked about any of these requests
const latestSync = (requests: SignatureRequest[]) =>
  requests.reduce<string | null>(
    (latest, { lastSyncedAt }) =>
      lastSyncedAt && (!latest || lastSyncedAt > latest)
        ? lastSyncedAt
        : latest,
    null
  )

/**
 * The e-signature requests sent to this applicant, and where each one stands.
 * The role check here only avoids a pointless request; the resolver is the gate
 * that actually enforces it.
 */
const ESignaturesSection = ({ applicantId }: Props) => {
  const { currentUser } = useAuth()
  const admin = isAdmin(currentUser)

  const { data } = useQuery(GET_SIGNATURE_REQUESTS, {
    variables: { applicantId },
    skip: !admin || !applicantId,
    fetchPolicy: 'cache-and-network',
    onError: () => toast('Failed to load e-signature requests', 'error'),
  })

  const [refresh, { loading: refreshing }] = useMutation(
    REFRESH_SIGNATURE_REQUESTS,
    {
      variables: { applicantId },
      // The mutation returns the same list the query shows: write it straight
      // in rather than asking the server for it a second time.
      update: (cache, { data: result }) => {
        if (!result) return
        cache.writeQuery({
          query: GET_SIGNATURE_REQUESTS,
          variables: { applicantId },
          data: { signatureRequests: result.refreshSignatureRequests },
        })
      },
      onError: (error) =>
        toast(error.message || 'Failed to refresh e-signatures', 'error'),
    }
  )

  if (!admin || !data) return null

  const requests: SignatureRequest[] = data.signatureRequests ?? []
  const synced = latestSync(requests)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-lg font-semibold text-doubleNickel-black">
            E-Signatures
            <AdminOnlyDot />
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{requests.length}</span>
            <span> request(s)</span>
            {synced && <span>{` · Updated ${formatDate(synced)}`}</span>}
          </div>
        </div>
        {requests.length > 0 && (
          <DNButton
            variant="secondary"
            loading={refreshing}
            onClick={() => refresh()}
          >
            <RefreshCw size={16} />
            Refresh
          </DNButton>
        )}
      </div>

      <DnTable
        columns={columns}
        data={requests}
        defaultResultsPerPage={20}
        emptyStateMessage="No e-signature requests have been sent to this applicant."
      />
    </div>
  )
}

export default ESignaturesSection
